import React, { useCallback, useEffect, useMemo } from "react";

import { isNaN, parseInt } from "lodash";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useGetTransitionProposal from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposal";
import { TransitionProposalId } from "@fartherfinance/frontend/api/TransitionProposals/types";

import UploadFilesProvider from "../../../DocumentsCenter/UploadFilesProvider/UploadFilesProvider";
import TransitionProposalFlow from "@src/multiCustodian/components/TransitionProposals/TransitionProposalFlow/TransitionProposalFlow";
import {
  TransitionProposalFlowPageIndex,
  TransitionProposalFlowStateData,
} from "@src/multiCustodian/components/TransitionProposals/types";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import { actions } from "@src/sharedComponents/FlowContainer/reducer/actions";
import { useFlowContainerContext } from "@src/sharedComponents/FlowContainer/reducer/Context";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";

const TransitionProposalFlowContainer: React.FC = () => {
  const { transitionProposalId } = useParams<{
    transitionProposalId: TransitionProposalId;
  }>();

  const [initialIndexLoaded, setInitialIndexLoaded] =
    React.useState<boolean>(false);

  const auth = useRequestAuth();

  const {
    state: { progressState },
    dispatch,
  } = useFlowContainerContext<Partial<TransitionProposalFlowStateData>>();

  const location = useLocation();

  const navigate = useNavigate();

  const freezePageIndex = useCallback(
    (index: number): number => {
      const maxIndex = progressState?.length ? progressState.length - 1 : 0;
      return Math.max(0, Math.min(maxIndex, index));
    },
    [progressState]
  );

  const {
    data: transitionProposalResponse,
    isLoading: isFetchingTransitionProposal,
  } = useGetTransitionProposal(transitionProposalId, auth);

  const transitionProposal = useMemo(
    () => transitionProposalResponse?.data,
    [transitionProposalResponse]
  );

  const initialPageIndex = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);

    const pageIndexParam = queryParams.get("pageIndex");

    const parsedPageIndex = parseInt(pageIndexParam ?? "", 10);

    const validPageIndex = freezePageIndex(
      isNaN(parsedPageIndex) ? 0 : parsedPageIndex
    );

    const { Review, Add, Share } = TransitionProposalFlowPageIndex;

    const isDraft = transitionProposal?.status === "draft";
    const isProcessed = transitionProposal?.status === "processed";
    const isShared = transitionProposal?.status === "shared";
    const isStatementImport = transitionProposal?.importType === "statement";
    const isManualImport = transitionProposal?.importType === "manual";
    const isExistingImport = transitionProposal?.importType === "existing";

    if (isShared) {
      return Share;
    }

    if (isProcessed) {
      return Review;
    }

    if (isStatementImport && isDraft) {
      return Add;
    }

    if (isManualImport && validPageIndex < Review) {
      return Review;
    }

    if (isExistingImport) {
      return Add;
    }

    return validPageIndex;
  }, [location.search, transitionProposal, freezePageIndex]);

  useEffect(() => {
    if (initialIndexLoaded || isFetchingTransitionProposal) {
      return;
    }

    navigate(
      {
        search: new URLSearchParams({
          pageIndex: initialPageIndex.toString(),
        }).toString(),
      },
      {
        replace: true,
      }
    );

    dispatch({
      type: actions.SET_DATA,
      payload: { importType: transitionProposal?.importType },
    });
    dispatch({ type: actions.SET_PAGE_INDEX, payload: initialPageIndex });
    setInitialIndexLoaded(true);
  }, [
    dispatch,
    initialPageIndex,
    transitionProposal,
    initialIndexLoaded,
    isFetchingTransitionProposal,
    navigate,
  ]);

  const isLoading =
    isFetchingTransitionProposal || transitionProposal === undefined;

  return (
    <UploadFilesProvider>
      {isLoading ? (
        <LogoLoadingStill onTop noBackgroundColor />
      ) : (
        <TransitionProposalFlow />
      )}
    </UploadFilesProvider>
  );
};

export default TransitionProposalFlowContainer;
